import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import { useQueries } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import { FeatureCollection } from "geojson";
import classnames from "classnames";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Map as MapGL } from "react-map-gl/mapbox";

import MarkerIcon from "@/assets/images/map-pin.png";
import {
  api,
  EarlyWarningTableWidgetConfigurationAllOfLayers,
  EarlyWarningTableWidgetDataWarningsInner,
} from "@/lib/api";
import { accountOwnedMapStyleUrl, knownMapboxStyles } from "@/lib/mapbox/base-layer-styles";
import { useDashboardData } from "@/features/shared/hooks";
import EarlyWarningSelectedWarning from "@/features/shared/widgets/early-warning-table/early-warning-selected-warning";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import LayerToggle from "@/features/shared/widgets/map/controls/layer-toggle";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import {
  ControlElement,
  ControlPosition,
  LayersPanelContainer,
} from "@/features/shared/widgets/map/map.styles";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";

import EarlyWarningMapTable from "./early-warning-map-table";
import {
  getGeoJsonLayerFromWarning,
  getIconLayerFromWarning,
  sortWarningsByLevel,
} from "./utils";

interface EarlyWarningMapProps {
  table: Table<{
    [date: string]: ReactNode;
    phenomenom: string;
  }>;
  selectedDay: number;
  setSelectedDay: (index: number) => void; //Dispatch<SetStateAction<number>>
  warnings: EarlyWarningTableWidgetDataWarningsInner[];
  selectedWarningId?: string;
  setSelectedWarning?: (id: string, dayIndex?: number) => void;
  layers: EarlyWarningTableWidgetConfigurationAllOfLayers[];
}

const EarlyWarningMap = ({
  table,
  selectedDay,
  setSelectedDay,
  warnings,
  selectedWarningId,
  setSelectedWarning,
  layers,
}: EarlyWarningMapProps) => {
  const { selectedDashboard, selectedTimeRange } = useDashboardData();

  const mapStyleUrl = accountOwnedMapStyleUrl(knownMapboxStyles.MonochromeLight);
  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapViewStateOptions = useMemo(
    () => ({
      containerRef: mapContainerRef,
      initialCoords: [
        { lat: 53.67885, lng: 3.273652 },
        { lat: 50.542863, lng: 7.279572 },
      ],
      fitBoundsOptions: {
        padding: {
          bottom: 200,
          top: 0,
          left: 0,
          right: 0,
        },
      },
    }),
    [mapContainerRef.current],
  );
  const { mapViewState } = useMapViewState(mapViewStateOptions);

  const [activeLayers, setActiveLayers] = useState<string[]>(
    layers.filter((layer) => layer.enabled_by_default).map((layer) => layer.id),
  );
  const handleToggleLayer = useCallback(
    (id: string) => () => {
      setActiveLayers((prev) =>
        prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id],
      );
    },
    [setActiveLayers],
  );

  const staticLayerQueries = useQueries({
    queries: layers.map((layer) => ({
      queryKey: [
        selectedDashboard?.id,
        layer.id,
        selectedTimeRange.timeStart,
        selectedTimeRange.timeEnd,
      ],
      queryFn: () =>
        api.published.getPublishedMapLayerData(
          selectedDashboard?.id ?? "",
          layer.id,
          selectedTimeRange.timeStart.toString(),
          selectedTimeRange.timeEnd.toString(),
        ),
    })),
  });

  const combinedLayers = useMemo(() => {
    const sortedWarnings = sortWarningsByLevel(warnings);
    const geojsonLayers = sortedWarnings
      .map((warning) => getGeoJsonLayerFromWarning(warning, selectedWarningId))
      .sort((warning) => (warning.id === selectedWarningId ? 1 : 0));
    const iconLayer = getIconLayerFromWarning(
      sortedWarnings,
      selectedWarningId,
      setSelectedWarning,
    );

    const staticLayers = staticLayerQueries.map((staticLayer, index) => {
      const data = staticLayer.data?.data as unknown as FeatureCollection;
      if (!data) return undefined;

      const id = layers[index].id;
      if (!activeLayers.includes(id)) return undefined;

      if (data?.features?.[0].geometry.type === "Point") {
        return new IconLayer({
          id: `icon-layer-${id}`,
          slot: "middle",

          data: data.features,
          getPosition: (d) => [
            Number(d.geometry.coordinates[0]),
            Number(d.geometry.coordinates[1]),
          ],
          getIcon: () => ({
            url: MarkerIcon,
            height: 64,
            width: 64,
            anchorX: 32,
            anchorY: 64,
          }),
          getSize: 32,
        });
      }

      const getColor = (color: string): [number, number, number] => {
        switch (color) {
          case "black":
            return [0, 0, 0];
          case "grey":
            return [200, 200, 200];
          case "white":
          default:
            return [255, 255, 255];
        }
      };

      return new GeoJsonLayer({
        id: `static-layer-${id}`,
        slot: "middle",
        data,
        filled: false,
        extruded: false,
        stroked: true,
        lineWidthMinPixels: 2,
        getLineColor: getColor(layers[index].color),
      });
    });

    return [...geojsonLayers, ...staticLayers, iconLayer];
  }, [warnings, selectedWarningId, staticLayerQueries, activeLayers]);

  const selectedWarning = useMemo(() => {
    return warnings.find((warning) => warning.id === selectedWarningId);
  }, [warnings, selectedWarningId]);

  const closeSelectedWarning = useCallback(() => {
    setSelectedWarning?.("");
  }, [setSelectedWarning]);

  return (
    <div ref={mapContainerRef} style={{ height: "calc(100% - 60px)" }}>
      {mapViewState && (
        <MapGL
          initialViewState={mapViewState}
          attributionControl={false}
          projection={{ name: "mercator" }}
          mapStyle={mapStyleUrl}
          mapboxAccessToken={mapTileAccessToken}
          maxPitch={0}
          bearingSnap={360}
          bearing={0}
        >
          <DeckGLOverlay interleaved controller layers={combinedLayers} />
          <div style={{ fontSize: "10px" }}>
            <EarlyWarningMapTable
              table={table}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />
          </div>

          {selectedWarning && (
            <EarlyWarningSelectedWarning
              warning={selectedWarning}
              closeSelectedWarning={closeSelectedWarning}
            />
          )}

          <ControlPosition>
            <ControlElement>
              <LayersPanelContainer className={classnames(panelCommonStyles.frostedPanel)}>
                {layers.map((layer) => (
                  <LayerToggle
                    key={layer.id}
                    title={layer.title}
                    toggle={handleToggleLayer(layer.id)}
                    isEnabled={activeLayers.includes(layer.id)}
                    isLoading={false}
                    isReady
                    isActive
                    isForecast={false}
                    settings={[]}
                    currentSettingValues={{}}
                    setSettingValue={() => {
                      /* no-op */
                    }}
                  />
                ))}
              </LayersPanelContainer>
            </ControlElement>
          </ControlPosition>
        </MapGL>
      )}
    </div>
  );
};

export default EarlyWarningMap;
